import type { WeeklyStatus } from '../../../lib/weeklyApi';
import type { TaskStatusCategory } from '../../../lib/taskStatusesApi';
import { useTaskStatuses } from '../../../hooks/useTaskStatuses';
import { StatusIcon } from '../../tasks/linear/taskVisuals';
import { QuickStatusIcon } from '../daily/MyDailyStatusSection';
import { useI18n } from '../../../contexts/I18nContext';
import { WEEKLY_STATUS_META } from './weeklyStatusMeta';

/**
 * Weekly buckets drawn in the task list's status language — the same 14px
 * ring / pie / disc icon and the configured status colour — so the weekly
 * page reads like the daily one instead of a row of pastel emoji chips.
 */
const CATEGORY: Record<Exclude<WeeklyStatus, 'BLOCKED'>, TaskStatusCategory> = {
  DONE: 'COMPLETED',
  IN_PROGRESS: 'STARTED',
  IN_REVIEW: 'STARTED',
  QA: 'STARTED',
  TODO: 'UNSTARTED',
  BACKLOG: 'BACKLOG',
  CANCELLED: 'CANCELLED',
};

const FALLBACK: Record<WeeklyStatus, string> = {
  DONE: '#22c55e',
  IN_PROGRESS: '#3b82f6',
  IN_REVIEW: '#8b5cf6',
  QA: '#14b8a6',
  TODO: '#9ca3af',
  BLOCKED: '#ef4444',
  BACKLOG: '#6b7280',
  CANCELLED: '#6b7280',
};

/** The colour of the configured status the bucket stands for (by slug). */
export function useWeeklyStatusColor(): (status: WeeklyStatus) => string {
  const { bySlug } = useTaskStatuses();
  return (status) => bySlug?.[status.toLowerCase()]?.color ?? FALLBACK[status];
}

export function WeeklyStatusIcon({
  status,
  color,
  size = 14,
}: {
  status: WeeklyStatus;
  color: string;
  size?: number;
}) {
  if (status === 'BLOCKED') return <QuickStatusIcon status="BLOCKED" color={color} size={size} />;
  return <StatusIcon category={CATEGORY[status]} color={color} size={size} />;
}

/** Icon + label in the status colour, e.g. inside "din To Do în Finalizat". */
export function WeeklyStatusLabel({
  status,
  muted = false,
  size = 12,
}: {
  status: WeeklyStatus;
  muted?: boolean;
  size?: number;
}) {
  const { t } = useI18n();
  const colorOf = useWeeklyStatusColor();
  const color = colorOf(status);
  return (
    <span
      className={`inline-flex items-center gap-1 font-medium ${muted ? 'text-gray-500' : ''}`}
      style={muted ? undefined : { color }}
    >
      <WeeklyStatusIcon status={status} color={muted ? '#9ca3af' : color} size={size} />
      {t(WEEKLY_STATUS_META[status].labelKey)}
    </span>
  );
}
